/**
 * Performance Debug Component
 * Adds a debug button to show performance metrics
 */

import { printPerformanceSummary, checkForLeaks, getPerformanceSummary } from '../utils/performance-monitor.js';
import { LOGGING } from '/settings.js';

let debugButton = null;

export function initializePerformanceDebug() {
    // Only show in development/debug mode
    if (!(LOGGING.ENABLED && LOGGING.DEBUG)) {
        return;
    }

    // Create debug button
    debugButton = document.createElement('button');
    debugButton.id = 'performance-debug-btn';
    debugButton.innerHTML = '🚀 Debug';
    debugButton.className = 'performance-debug-btn';
    debugButton.title = 'Show Performance Metrics';

    // Style the button
    Object.assign(debugButton.style, {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: '9999',
        padding: '8px 12px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        fontFamily: 'monospace',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    });

    // Add hover effect
    debugButton.addEventListener('mouseenter', () => {
        debugButton.style.backgroundColor = '#0056b3';
    });

    debugButton.addEventListener('mouseleave', () => {
        debugButton.style.backgroundColor = '#007bff';
    });

    // Add click handler
    debugButton.addEventListener('click', () => {
        showPerformanceModal();
    });

    // Add to page
    document.body.appendChild(debugButton);
}

function showPerformanceModal() {
    if (!(LOGGING.ENABLED && LOGGING.DEBUG)) return;
    // Remove existing modal if any
    const existingModal = document.getElementById('performance-modal');
    if (existingModal) {
        existingModal.remove();
    }

    // Create modal
    const modal = document.createElement('div');
    modal.id = 'performance-modal';
    modal.className = 'performance-modal';

    // Style modal
    Object.assign(modal.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: '10000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    });

    // Create modal content
    const content = document.createElement('div');
    content.className = 'performance-modal-content';

    Object.assign(content.style, {
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: '14px'
    });

    // Get performance data
    const summary = getPerformanceSummary();
    const warnings = checkForLeaks();

    // Create content HTML
    content.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
            <h3 style="margin: 0; color: #333;">🚀 Performance Metrics</h3>
            <button id="close-performance-modal" style="background: none; border: none; font-size: 20px; cursor: pointer;">×</button>
        </div>
        
        <div style="margin-bottom: 20px;">
            <h4 style="margin: 0 0 10px 0; color: #666;">📊 Summary</h4>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                <div><strong>Runtime:</strong> ${summary.runtime}</div>
                <div><strong>Markers Created:</strong> ${summary.markersCreated}</div>
                <div><strong>Markers Removed:</strong> ${summary.markersRemoved}</div>
                <div><strong>Spots Loaded:</strong> ${summary.spotsLoaded}</div>
                <div><strong>DOM Operations:</strong> ${summary.domOperations}</div>
                ${summary.currentMemory ? `<div><strong>Memory:</strong> ${summary.currentMemory}</div>` : ''}
            </div>
        </div>

        ${warnings.length > 0 ? `
            <div style="margin-bottom: 20px;">
                <h4 style="margin: 0 0 10px 0; color: #dc3545;">⚠️ Warnings</h4>
                <ul style="margin: 0; padding-left: 20px; color: #dc3545;">
                    ${warnings.map(warning => `<li>${warning}</li>`).join('')}
                </ul>
            </div>
        ` : ''}

        <div style="margin-bottom: 20px;">
            <h4 style="margin: 0 0 10px 0; color: #666;">🔧 Actions</h4>
            <div style="display: flex; gap: 10px;">
                <button id="print-summary" style="padding: 8px 12px; background: #28a745; color: white; border: none; border-radius: 4px; cursor: pointer;">Print to Console</button>
                <button id="check-leaks" style="padding: 8px 12px; background: #ffc107; color: #333; border: none; border-radius: 4px; cursor: pointer;">Check for Leaks</button>
            </div>
        </div>

        <div style="font-size: 12px; color: #999;">
            <p><strong>Tip:</strong> Open browser DevTools and check the Console tab for detailed logs.</p>
            <p><strong>Memory:</strong> Use Chrome DevTools Memory tab to analyze memory usage.</p>
        </div>
    `;

    // Add event listeners
    content.querySelector('#close-performance-modal').addEventListener('click', () => {
        modal.remove();
    });

    content.querySelector('#print-summary').addEventListener('click', () => {
        printPerformanceSummary();
    });

    content.querySelector('#check-leaks').addEventListener('click', () => {
        const newWarnings = checkForLeaks();
        if (newWarnings.length > 0) {
            alert('⚠️ Performance warnings detected!\n\n' + newWarnings.join('\n'));
        } else {
            alert('✅ No performance issues detected!');
        }
    });

    // Close modal when clicking outside
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            modal.remove();
        }
    });

    // Add to page
    modal.appendChild(content);
    document.body.appendChild(modal);
}

// Auto-initialize when DOM is ready
if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializePerformanceDebug);
    } else {
        initializePerformanceDebug();
    }
} 